import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, BarChart3, Users, Eye, CalendarDays, TrendingUp, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_COLORS = {
  Direct: "#9CA3AF", Google: "#F2B705", Facebook: "#60A5FA", Instagram: "#F472B6",
  "Twitter/X": "#38BDF8", TikTok: "#34D399", YouTube: "#F87171", Email: "#A78BFA",
};
const colorFor = (s) => SOURCE_COLORS[s] || "#F2B705";

function StatBox({ icon: Icon, label, value, tone = "white" }) {
  const tones = { white: "text-white", gold: "text-[#F2B705]", ok: "text-green-400" };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-widest"><Icon size={14} /> {label}</div>
      <div className={`font-display font-black text-3xl mt-1.5 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function AnalyticsManager({ token, onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  }, [token, onAuthError]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load analytics.</p>;

  const s = data.stats;
  const maxDay = Math.max(1, ...data.series.map((d) => d.count));
  const maxSrc = Math.max(1, ...data.sources.map((d) => d.count));

  return (
    <div data-testid="analytics-dashboard" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><BarChart3 className="text-[#F2B705]" size={22} /> Website Traffic</h2>
        <p className="text-white/45 text-sm mt-1">Every visit to your site is counted here, with where the visitor came from.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox icon={Eye} label="Total Visits" value={s.total_visits} tone="gold" />
        <StatBox icon={Users} label="Unique Visitors" value={s.unique_visitors} />
        <StatBox icon={CalendarDays} label="Today" value={s.today_visits} tone="ok" />
        <StatBox icon={TrendingUp} label="Last 7 Days" value={s.week_visits} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-white/60" /> Visits — Last 7 Days</h3>
          <div className="flex items-end justify-between gap-2 h-44" data-testid="analytics-chart">
            {data.series.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
                <span className="text-xs text-white/60">{d.count}</span>
                <div className="w-full bg-[#F2B705] rounded-t transition-all" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count > 0 ? "4px" : "0" }} />
                <span className="text-[10px] text-white/40">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Globe size={18} className="text-white/60" /> Where Visitors Came From</h3>
          {data.sources.length === 0 ? (
            <p className="text-white/35 text-sm">No traffic data yet. Visits will appear here as people hit your site.</p>
          ) : (
            <div className="space-y-3" data-testid="analytics-sources">
              {data.sources.map((src) => (
                <div key={src.source}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white/80">{src.source}</span>
                    <span className="text-white/50">{src.count} ({Math.round((src.count / s.total_visits) * 100)}%)</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(src.count / maxSrc) * 100}%`, backgroundColor: colorFor(src.source) }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.top_pages.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
          <h3 className="font-display font-bold text-lg mb-3">Top Pages</h3>
          <div className="space-y-2" data-testid="analytics-pages">
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex justify-between items-center text-sm border-b border-white/5 pb-1.5 last:border-0">
                <span className="text-white/75 truncate">{p.path}</span>
                <span className="text-[#F2B705] font-semibold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
